import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { Button, Field, Input, Spinner } from "@agentsaas/ui";
import { useErrorText, useT } from "../app/i18n";
import { useSession } from "../app/session";
import { useServices } from "../app/services";
import { LanguageSelect, ThemeToggle } from "../layout/shell";
import { DEMO_EMAIL, DEMO_PASSWORD } from "../mock/constants";

function AuthFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__top">
          <LanguageSelect compact />
          <ThemeToggle />
        </div>
        <div className="card">
          <h1 className="auth__title">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function useNext() {
  const [sp] = useSearchParams();
  const n = sp.get("next");
  return n && n.startsWith("/") && !n.startsWith("//") ? n : "/";
}

export function LoginPage() {
  const { t } = useT();
  const errText = useErrorText();
  const session = useSession();
  const { config } = useServices();
  const navigate = useNavigate();
  const next = useNext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (session.state.status === "authed") return <Navigate to={next} replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await session.login({ email, password, mode: "cookie" });
      navigate(next, { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame title={t("auth.login.title")}>
      <form onSubmit={submit} noValidate>
        <Field label={t("auth.email")}>{(id) => <Input id={id} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />}</Field>
        <Field label={t("auth.password")}>
          {(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />}
        </Field>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block loading={busy} data-testid="login-submit">
          {t("auth.login.submit")}
        </Button>
      </form>
      {config.mock ? <p className="muted small">{t("auth.demo_hint", { email: DEMO_EMAIL, password: DEMO_PASSWORD })}</p> : null}
      <p className="auth__alt">
        <Link to="/register">{t("auth.to_register")}</Link>
      </p>
    </AuthFrame>
  );
}

export function RegisterPage() {
  const { t, locale } = useT();
  const errText = useErrorText();
  const session = useSession();
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: "", email: "", password: "", inviteCode: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (session.state.status === "authed") return <Navigate to="/" replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { inviteCode, ...rest } = form;
      await session.register({ ...rest, locale, ...(inviteCode.trim() ? { inviteCode: inviteCode.trim() } : {}) });
      navigate("/", { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <AuthFrame title={t("auth.register.title")}>
      <form onSubmit={submit} noValidate>
        <Field label={t("auth.display_name")}>{(id) => <Input id={id} autoComplete="nickname" value={form.displayName} onChange={set("displayName")} maxLength={64} />}</Field>
        <Field label={t("auth.email")}>{(id) => <Input id={id} type="email" autoComplete="email" value={form.email} onChange={set("email")} />}</Field>
        <Field label={t("auth.password")} hint={t("auth.password_hint")}>
          {(id, d) => <Input id={id} type="password" autoComplete="new-password" aria-describedby={d} value={form.password} onChange={set("password")} minLength={8} />}
        </Field>
        <Field label={t("auth.invite_code")} hint={t("auth.invite_code_hint")}>
          {(id, d) => <Input id={id} autoComplete="off" aria-describedby={d} value={form.inviteCode} onChange={set("inviteCode")} maxLength={128} data-testid="register-invite" />}
        </Field>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block loading={busy}>
          {t("auth.register.submit")}
        </Button>
      </form>
      <p className="auth__alt">
        <Link to="/login">{t("auth.to_login")}</Link>
      </p>
    </AuthFrame>
  );
}

/** 需要登录的路由 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useT();
  const session = useSession();
  const loc = useLocation();
  if (session.state.status === "loading")
    return (
      <div className="auth">
        <Spinner label={t("common.loading")} />
      </div>
    );
  if (session.state.status === "anon") return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}
